import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, User, Mail, Sparkles } from "lucide-react";
import { MemberHeader } from "@/components/MemberHeader";
import { Reveal } from "@/components/Reveal";
import { findCity } from "@/config/site";
import { getAccount, requestAccountLink, STATUS_LABELS, frDate } from "@/services/signup";

const TOKEN_KEY = "altitude_account_token";

const CONTACT = "contact@club-altitude.com";

const Item = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-4 border-b border-navy/10 py-5 last:border-0">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep" strokeWidth={1.5} />
    <div>
      <p className="text-xs uppercase tracking-[0.16em] text-navy/45">{label}</p>
      <p className="mt-1 text-[15px] text-navy" data-testid={testId}>{value}</p>
    </div>
  </div>
);

const statusText = (s) => {
  if (!s) return null;
  const parts = [STATUS_LABELS[s.status] || s.status];
  if (s.status.startsWith("trial") && s.trial_end) parts.push(`jusqu'au ${frDate(s.trial_end)}`);
  if (s.status.endsWith("cancelling") && (s.access_until || s.current_period_end)) parts.push(`fin des droits le ${frDate(s.access_until || s.current_period_end)}`);
  if (s.status === "active" && s.current_period_end) parts.push(`renouvellement le ${frDate(s.current_period_end)}`);
  return parts.join(" · ");
};

const ManageLink = ({ url }) => (url ? (
  <a href={url} data-testid="account-manage-link" className="mt-6 inline-block rounded-full border border-navy/20 px-5 py-2.5 text-sm text-navy transition-colors duration-300 hover:border-navy/40">
    Gérer ou résilier mon abonnement
  </a>
) : (
  <p data-testid="account-manage-contact" className="mt-4 text-sm text-navy/70">
    Pour résilier, écrivez à <a href={`mailto:${CONTACT}?subject=${encodeURIComponent("Résiliation de mon abonnement")}`} className="underline decoration-gold underline-offset-4">{CONTACT}</a>.
  </p>
));

const card = "mt-8 rounded-[28px] bg-white p-6 shadow-[0_30px_60px_-40px_rgba(11,21,48,0.45)] sm:p-8";

const LinkRequest = ({ notice }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setState("invalid");
    setState("sending");
    try { await requestAccountLink(email.trim()); setState("sent"); } catch { setState("error"); }
  };
  if (state === "sent") {
    return <Reveal className={card}><p data-testid="account-link-sent" className="text-[15px] leading-relaxed text-navy">Si un abonnement est associé à cette adresse, vous allez recevoir un e-mail avec votre lien d'accès, valable 24 heures. Pensez à vérifier vos courriers indésirables.</p></Reveal>;
  }
  return (
    <Reveal className={card}>
      {notice && <p data-testid="account-link-notice" className="mb-4 text-sm text-red-700">{notice}</p>}
      <p className="text-[15px] leading-relaxed text-navy/80">Indiquez l'adresse e-mail de votre inscription. Nous vous enverrons un lien pour consulter, gérer ou résilier votre abonnement.</p>
      <form onSubmit={submit} noValidate className="mt-6 space-y-3" data-testid="account-link-form">
        <label htmlFor="account-email-input" className="text-sm text-navy">Adresse e-mail</label>
        <input id="account-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => { setEmail(e.target.value); setState("idle"); }} data-testid="account-email-input" className="w-full rounded-2xl border border-navy/15 bg-ivory/60 px-4 py-3.5 text-[15px] text-navy outline-none transition-colors duration-300 focus:border-gold" />
        {state === "invalid" && <p data-testid="account-email-error" className="text-sm text-red-700">Indiquez une adresse e-mail valide.</p>}
        {state === "error" && <p data-testid="account-link-error" className="text-sm text-red-700">L'envoi n'a pas pu aboutir. Réessayez dans un instant.</p>}
        <button type="submit" disabled={state === "sending"} data-testid="account-link-submit" className="btn-navy w-full justify-center py-3.5 disabled:opacity-60">
          {state === "sending" ? "Envoi…" : "Recevoir mon lien d'accès"}
        </button>
      </form>
      <p className="mt-5 text-sm text-navy/60">Pas encore membre ? <Link to="/inscription" data-testid="account-register-link" className="underline decoration-gold underline-offset-4">Rejoindre le club</Link></p>
    </Reveal>
  );
};

const AccountDetails = ({ acc, onLeave }) => {
  const city = findCity(acc.city);
  const name = [acc.civility, acc.first_name, acc.last_name].filter(Boolean).join(" ");
  return (
    <>
      <Reveal className={card}>
        <Item icon={User} label="Membre" value={name || "—"} testId="account-name" />
        <Item icon={Mail} label="E-mail" value={acc.email} testId="account-email" />
        <Item icon={MapPin} label="Aéroport de départ" value={city ? `${city.airport}, ${city.country}` : acc.city} testId="account-departure" />
        <Item icon={Sparkles} label="Destination de rêve" value={acc.dream || "—"} testId="account-dream" />
      </Reveal>
      <Reveal delay={0.1} className="mt-5 rounded-[28px] border border-navy/10 bg-white/60 p-6 sm:p-8">
        <h2 className="font-display text-2xl text-navy">Abonnement</h2>
        <p data-testid="account-status" className="mt-3 text-sm leading-relaxed text-navy/70">{statusText(acc)}</p>
        {acc.status === "pending_payment" && (
          <Link to="/inscription" data-testid="account-finalize-link" className="mt-4 inline-block text-sm text-navy underline decoration-gold underline-offset-4">Finaliser mon inscription</Link>
        )}
        {acc.status !== "pending_payment" && acc.status !== "cancelled" && <ManageLink url={acc.manage_url} />}
      </Reveal>
      <button type="button" onClick={onLeave} data-testid="account-leave-button" className="mt-6 text-sm text-navy/60 underline underline-offset-4 hover:text-navy">Quitter mon compte sur cet appareil</button>
    </>
  );
};

export default function Account() {
  const [params, setParams] = useSearchParams();
  const [acc, setAcc] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    const t = params.get("t") || sessionStorage.getItem(TOKEN_KEY);
    if (params.get("t")) setParams({}, { replace: true });
    if (!t) { setState("ask"); return; }
    getAccount(t)
      .then((a) => { sessionStorage.setItem(TOKEN_KEY, t); setAcc(a); setState("ready"); })
      .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setState("expired"); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const leave = () => { sessionStorage.removeItem(TOKEN_KEY); setAcc(null); setState("ask"); };

  return (
    <div className="min-h-screen bg-ivory">
      <MemberHeader />
      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <p className="eyebrow text-gold-deep">Espace membre</p>
        <h1 className="mt-3 font-display text-4xl text-navy sm:text-5xl">Mon compte</h1>
        {state === "loading" && <p data-testid="account-loading" className="mt-8 text-sm text-navy/60">Chargement…</p>}
        {state === "ready" && acc && <AccountDetails acc={acc} onLeave={leave} />}
        {(state === "ask" || state === "expired") && <LinkRequest notice={state === "expired" ? "Ce lien a expiré ou n'est plus valide. Demandez-en un nouveau ci-dessous." : ""} />}
      </main>
    </div>
  );
}
